import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../atoms/Button';
import { SellerToList } from './types';
import { useSellersColumns } from './Columns';
import Search from '../categories/Search';
import dayjs from 'dayjs';
import CustomTable from '../atoms/CustomTable';
import {
  useGetSellersQuery,
  useDeleteSellerMutation,
} from '../../store/services/addSellerService';
import DeleteSellerModal from './DeleteSellerModal';
import { message } from 'antd';
import { usePublicState } from '../../store/slices/publicSlice';
import TableWrapper from '../atoms/TableWrapper';
import useHandleApiError from '../../hooks/useHandleApiError';

const SellersTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { search } = usePublicState();
  const sellersSearch = search['sellersSearch'] || undefined;
  dayjs.locale(language);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sellerToDelete, setSellerToDelete] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useGetSellersQuery({
    page: page,
    limit: 10,
    search: sellersSearch,
  });

  const [deleteSeller, { isLoading: isDeleting }] = useDeleteSellerMutation();
  const { handleApiError } = useHandleApiError();

  const handleDeleteSeller = (id: string) => {
    setSellerToDelete(id);
    setDeleteModalOpen(true);
  };

  const columns = useSellersColumns(handleDeleteSeller);

  const apiSellersList = data?.payload?.content || [];
  const sellersList = apiSellersList.map((seller: any) => {
    return {
      id: seller.id || seller.profileId,
      sellerId: seller.serial ? `#${seller.serial}` : '',
      name: seller.sellerName || '',
      email: seller.email || '',
      profileComplete: seller.isCompleted,
      dateApplied: seller.createdDate
        ? dayjs(seller.createdDate).format('DD MMM YYYY')
        : '-',
      actions: seller.actions,
    };
  });

  const totalElements = data?.payload?.totalElements || 0;

  return (
    <TableWrapper
      filters={
        <div
          className="flex items-center gap-4"
          data-testid="sellers-table-actions"
        >
          <div data-testid="sellers-table-search-wrapper">
            <Search
              searchName="sellersSearch"
              inputWidth="!w-[330px] !h-[46px] !border-[#E7E7E9] dark:!border-dark-input-border !rounded-[4px]"
            />
          </div>
          <Button
            data-testid="add-seller-button"
            type="button"
            variant="primary"
            onClick={() => navigate('/sellers/addSeller')}
            className="!h-[46px] !px-[24px] !rounded-[12px] !text-[16px] !font-semibold !font-sans"
          >
            {t('sellers.addUser')}
          </Button>
        </div>
      }
      title={t('sellers.listTitle')}
      subTitle={t('sellers.listDesc')}
    >
      <div data-testid="sellers-table-wrapper">
        <CustomTable
          columns={columns}
          dataSource={sellersList}
          currentPage={page}
          onPageChange={(page) => setPage(page - 1)}
          totalElements={totalElements}
          loading={isLoading || isFetching}
          rowKey="id"
          onRowClick={(_, record: SellerToList) => {
            if (record.profileComplete) {
              navigate(`/sellers/${record.id}/profile`);
            } else {
              navigate(`/sellers/${record.id}/profile-creation`);
            }
          }}
        />
      </div>
      <DeleteSellerModal
        isOpen={deleteModalOpen}
        isLoading={isDeleting}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={async () => {
          if (sellerToDelete) {
            try {
              await deleteSeller(sellerToDelete).unwrap();
              message.success(t('sellers.deleteSuccess'));
              refetch();
            } catch (err: unknown) {
              handleApiError(err);
            }
          }
          setDeleteModalOpen(false);
          setSellerToDelete(null);
        }}
      />
    </TableWrapper>
  );
};

export default SellersTable;
